import { z } from "zod";
import { Form } from "@/app/global-components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import LoginFieldView from "./login-field-view";
import LoginSubmitView from "./login-submit-view";
import { useNavigate } from "react-router-dom";
import { storeData } from "@/lib/utils";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import { routes } from "@/consts";
import useLogin from "@/hooks/use-login";
import { showToast } from "@/app/global-components/toaster";
import { useEffect } from "react";

const LoginFormView = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const navigate = useNavigate();
  const { login, isLoading, error } = useLogin();

  useEffect(() => {
    if (error) {
      showToast(String(error), "error");
    }
  }, [error]);

  const LoginSchema = z.object({
    email: z
      .string()
      .email({ message: t("authentication.validation.invalidEmail") }),
    password: z.string().min(8, {
      message: t("authentication.changePassword.validation.passwordLength"),
    }),
  });
  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof LoginSchema>) => {
    try {
      const response = await login(values);
      if (response.isSuccess) {
        storeData("tokens", response.data.tokens);
        storeData("userInfo", response.data.user);
        navigate(routes.home);
      } else {
        showToast(response.message, "error");
      }
    } catch (err) {
      const error = err as Error;
      console.log(error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <LoginFieldView
          name="email"
          label={t("profile.fields.email")}
          placeholder={t("profile.fields.email")}
          inputType="email"
          formControl={form.control}
        />
        <LoginFieldView
          name="password"
          label={t("authentication.fields.password")}
          placeholder={t("authentication.fields.password")}
          description={t("authentication.validation.passwordDesc")}
          inputType="password"
          formControl={form.control}
        />

        <LoginSubmitView isLoading={isLoading} />
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      </form>
    </Form>
  );
};

export default LoginFormView;
